import { ReactNode } from "react";

interface V2PageHeaderProps {
  eyebrow: string;
  title: ReactNode;
  subtitle?: ReactNode;
  // Optional accent slot for trailing chips/buttons (right side under hero).
  trailing?: ReactNode;
  className?: string;
  // b17e: every top-level page header now uses one uniform title size so the
  // menus read as a set. The default clamp is tuned so the LONGEST headline —
  // currently "Potential Trades. Discover." — still fits on a single line at
  // the 375pt iPhone width. It matters: the h1 is nowrap + ellipsis, so a
  // headline that outgrows the clamp is TRUNCATED rather than wrapped. Check a
  // new headline against 375pt before adding it. Pages no longer pass a
  // per-page clamp; the prop is kept only as an escape hatch.
  titleClamp?: string;
  // b18a item 3: read off the element by DotRail to opt the page header out of
  // the per-block spine dot (it already carries its own eyebrow dot). Not
  // rendered to the DOM.
  "data-dotrail-skip"?: boolean;
}

// Shared hero header used by every secondary page. Renders the eyebrow
// pill, the Space Grotesk display title, and an optional subtitle.
// The page wrapper provides the background; we only render text here.
export function V2PageHeader({
  eyebrow,
  title,
  subtitle,
  trailing,
  className = "",
  titleClamp = "clamp(20px, 5.6vw, 28px)",
}: V2PageHeaderProps) {
  return (
    <section
      className={`pt-1 ${className}`}
      style={{ paddingBottom: "var(--space-page-header-gap)" }}
      data-testid="section-page-header"
    >
      <div className="v2-eyebrow inline-flex" data-testid="page-eyebrow">
        <span className="v2-eyebrow__dot" />
        {eyebrow}
      </div>
      <h1
        className="v2-display v2-page-title mt-3 leading-[1.05] whitespace-nowrap overflow-hidden text-ellipsis"
        style={{
          fontSize: titleClamp,
          letterSpacing: "-0.01em",
        }}
        data-testid="page-title"
      >
        {title}
      </h1>
      {subtitle && (
        <p
          className="v2-page-subtitle mt-2 text-[12.5px] leading-relaxed"
          data-testid="page-subtitle"
        >
          {subtitle}
        </p>
      )}
      {trailing && <div className="mt-3">{trailing}</div>}
    </section>
  );
}

// Inline gradient accent for the "key word" of a headline. Uses the
// unified Nexus green gradient (light mode -> darker stops for AA contrast
// on white; dark mode -> bright neon stops for the dex board aesthetic).
export function V2Accent({ children }: { children: ReactNode }) {
  return (
    <span className="v2-accent-green">
      {children}
    </span>
  );
}
